import React, { useEffect, useRef, useState, useCallback } from 'react';
import { chatService } from '../../api';
import { signalRService } from '../../signalRService';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import './ChatWindow.css';

export default function ChatWindow({ chat, currentUserId }) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const bottomRef = useRef(null);
    const PAGE_SIZE = 50;

    const loadMessages = useCallback(async (p = 1) => {
        try {
            const { data } = await chatService.getMessages(chat.id, p);
            if (p === 1) {
                setMessages(data);
            } else {
                setMessages(prev => [...data, ...prev]);
            }
            if (data.length < PAGE_SIZE) setHasMore(false);
        } catch (e) {
            console.error('Failed to load messages', e);
        } finally {
            setLoading(false);
        }
    }, [chat.id]);

    useEffect(() => {
        setMessages([]);
        setPage(1);
        setHasMore(true);
        setLoading(true);
        loadMessages(1);
        chatService.markAsRead(chat.id).catch(() => {});
    }, [chat.id, loadMessages]);

    useEffect(() => {
        const handler = (msg) => {
            if (msg.chatId !== chat.id) return;
            setMessages(prev => [...prev, msg]);
            chatService.markAsRead(chat.id).catch(() => {});
        };
        signalRService.on('ReceiveMessage', handler);
        return () => signalRService.off('ReceiveMessage', handler);
    }, [chat.id]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (content) => {
        try {
            await signalRService.invoke('SendMessage', chat.id, content);
        } catch {
            // Fallback to REST
            const { data } = await chatService.sendMessage(chat.id, content);
            setMessages(prev => [...prev, data]);
        }
    };

    const handleAttach = async (file) => {
        try {
            // Send empty message first, then attach
            const { data: msg } = await chatService.sendMessage(chat.id, '');
            const { data: att } = await chatService.uploadAttachment(chat.id, msg.id, file);
            setMessages(prev => prev.map(m =>
                m.id === msg.id
                    ? { ...m, attachments: [...(m.attachments || []), att] }
                    : m
            ));
        } catch (e) {
            console.error('Attachment upload failed', e);
        }
    };

    const loadMore = () => {
        const next = page + 1;
        setPage(next);
        loadMessages(next);
    };

    const otherParticipant = chat.participants.find(p => p.userId !== currentUserId);
    const chatName = chat.isGroup ? chat.name : otherParticipant?.userName;

    return (
        <div className="chat-window">
            <div className="chat-window-header">
                <div className="chat-window-avatar">
                    {otherParticipant?.avatarUrl
                        ? <img src={otherParticipant.avatarUrl} alt={chatName} />
                        : <div className="chat-window-avatar-placeholder">{chatName?.[0]?.toUpperCase()}</div>
                    }
                </div>
                <span className="chat-window-name">{chatName}</span>
            </div>

            <div className="chat-window-messages">
                {loading && <div className="chat-window-loading">Загрузка...</div>}
                {!loading && hasMore && (
                    <button className="chat-window-load-more" onClick={loadMore}>
                        Загрузить ещё
                    </button>
                )}
                {messages.map(msg => (
                    <MessageBubble
                        key={msg.id}
                        message={msg}
                        isOwn={msg.senderId === currentUserId}
                    />
                ))}
                <div ref={bottomRef} />
            </div>

            <MessageInput onSend={handleSend} onAttach={handleAttach} />
        </div>
    );
}
